import React, { createContext, useState, useContext, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useAppContext, APP_PAGE_STORAGE_KEY } from './AppContext';
import { clearLocalAuthSession, supabase, AUTH_SESSION_INVALID_EVENT, recoverStaleAuthSession } from '../lib/supabase';
import { notify } from '../utils/toast';
import {
  buildInvitePath,
  clearInviteToken,
  getPendingInviteToken,
  isInviteRoute,
  persistInviteToken,
  redirectLegacyInviteUrl,
} from '../services/modules/organizationService';
import { captureEvent } from '../lib/analytics/posthog';
import { mfaPathForGate, resolveMfaGate, type MfaGate } from '../lib/auth/mfaGate';
import { getSlugFromPage, isAuthPath, resolvePostAuthDestination } from '../utils/routeUtils';
import { isSuperadminRole } from '../domain/superadmin/guards';
import {
  hasPendingOnboarding,
  markPendingOnboarding,
} from '../features/onboarding/pendingOnboarding';
import {
  consumeAuthReturnPath,
  consumeOAuthAuthorizeReturn,
  sanitizeOAuthAuthorizeReturn,
  setOAuthAuthorizeReturn,
} from '../lib/auth/authReturnPath';

/** `invite` evita no-op ao ir para login: authPage default já era `login` (pushState sem re-render). */
type AuthPage = 'login' | 'register' | 'invite';
type AuthNavPage = 'login' | 'register';

interface AuthContextType {
  isAuthenticated: boolean;
  authPage: AuthPage;
  login: () => void | Promise<void>;
  logout: () => void;
  navigateToRegister: () => void;
  navigateToLogin: () => void;
  /** Sai de /convite com re-render do AppRouter (replaceState sozinho não remonta). */
  exitInviteToApp: () => void;
  user: User | null;
  isAuthLoading: boolean;
  mfaGate: MfaGate;
  refreshMfaGate: () => Promise<MfaGate>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_PATHS: Record<AuthNavPage, string> = { login: '/login', register: '/registro' };
const TOKEN_REFRESH_REVOKED = 'TOKEN_REFRESH_REVOKED';
const ONBOARDING_PATH = `/${getSlugFromPage('onboarding')}`;

function storagePage(mode: 'get' | 'clear'): string | null {
  try {
    if (mode === 'clear') {
      localStorage.removeItem(APP_PAGE_STORAGE_KEY);
      return null;
    }
    return localStorage.getItem(APP_PAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function replaceHistory(path: string): void {
  window.history.replaceState(null, '', path);
}

function inviteQuery(): string {
  const token = getPendingInviteToken();
  return token ? `?token=${encodeURIComponent(token)}` : '';
}

function currentPathWithQuery(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** No-op unless the address bar holds a valid `/oauth/authorize?…` consent request. */
function rememberPendingOAuthAuthorize(): void {
  setOAuthAuthorizeReturn(currentPathWithQuery());
}

function isOnOAuthAuthorize(): boolean {
  return sanitizeOAuthAuthorizeReturn(currentPathWithQuery()) !== null;
}

function redirectToLoginPreservingInvite() {
  if (isInviteRoute() || window.location.pathname === AUTH_PATHS.login) return;
  rememberPendingOAuthAuthorize();
  replaceHistory(`${AUTH_PATHS.login}${inviteQuery()}`);
}

function redirectAfterSessionLoss() {
  storagePage('clear');
  redirectToLoginPreservingInvite();
}

function authPageFromPath(path: string): AuthPage | null {
  if (path === '/register' || path === AUTH_PATHS.register) return 'register';
  if (path === AUTH_PATHS.login) return 'login';
  if (path === '/convite') return 'invite';
  return null;
}

function initialAuthPage(): AuthPage {
  return authPageFromPath(window.location.pathname.replace(/\/$/, '')) ?? 'login';
}

function goToOnboardingRoute(setCurrentPage: (page: 'onboarding') => void): void {
  setCurrentPage('onboarding');
  replaceHistory(ONBOARDING_PATH);
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authPage, setAuthPage] = useState<AuthPage>(initialAuthPage);
  const [user, setUser] = useState<User | null>(null);
  const [mfaGate, setMfaGate] = useState<MfaGate>('checking');

  const { setCurrentPage } = useAppContext();
  const hadSessionRef = useRef(false);
  const skipSessionExpiredToastRef = useRef(false);
  const sessionExpiredToastShownRef = useRef(false);
  const authReadyRef = useRef(false);
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  const clearClientAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setMfaGate('checking');
  }, []);

  const refreshMfaGate = useCallback(async () => {
    const gate = await resolveMfaGate();
    const mfaPath = mfaPathForGate(gate);
    if (mfaPath) rememberPendingOAuthAuthorize();
    const oauthReturn = gate === 'passed' ? consumeOAuthAuthorizeReturn() : null;
    if (oauthReturn && currentPathWithQuery() !== oauthReturn) replaceHistory(oauthReturn);
    setMfaGate(gate);
    if (oauthReturn) return gate;
    if (mfaPath && window.location.pathname !== mfaPath) {
      replaceHistory(mfaPath);
    } else if (gate === 'passed' && isAuthPath(window.location.pathname) && !isInviteRoute()) {
      if (hasPendingOnboarding()) {
        goToOnboardingRoute(setCurrentPage);
      } else {
        let isSuperadmin = false;
        const uid = userRef.current?.id;
        if (uid) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('global_role')
            .eq('id', uid)
            .maybeSingle();
          isSuperadmin = isSuperadminRole(profile?.global_role);
        }
        const { path, page } = resolvePostAuthDestination(
          window.location.pathname,
          storagePage('get'),
          { isSuperadmin, returnPath: consumeAuthReturnPath() },
        );
        replaceHistory(path);
        setCurrentPage(page);
      }
    }
    return gate;
  }, [setCurrentPage]);

  const applySession = useCallback((nextSession: Session | null) => {
    setUser(nextSession?.user ?? null);
    setIsAuthenticated(!!nextSession);
    if (nextSession) {
      hadSessionRef.current = true;
      void refreshMfaGate();
      return;
    }
    setMfaGate('checking');
  }, [refreshMfaGate]);

  const goToAuthPage = useCallback((page: AuthNavPage) => {
    setAuthPage(page);
    window.history.pushState(null, '', `${AUTH_PATHS[page]}${inviteQuery()}`);
  }, []);

  useEffect(() => {
    if (!user?.id || isInviteRoute()) return;
    const token = getPendingInviteToken();
    if (token) {
      replaceHistory(buildInvitePath(token));
      setAuthPage('invite');
    }
  }, [user?.id]);

  useEffect(() => {
    const pendingInvite = getPendingInviteToken();
    if (pendingInvite) persistInviteToken(pendingInvite);
    rememberPendingOAuthAuthorize();

    redirectLegacyInviteUrl();
    const path = window.location.pathname.replace(/\/$/, '');
    const page = authPageFromPath(path);
    if (page) setAuthPage(page);

    const finishAuthLoading = () => {
      if (!authReadyRef.current) {
        authReadyRef.current = true;
        setIsAuthLoading(false);
      }
    };

    const settleSessionEnd = (notifyExpired: boolean) => {
      finishAuthLoading();
      const hadSession = hadSessionRef.current;
      hadSessionRef.current = false;
      if (
        hadSession &&
        notifyExpired &&
        !skipSessionExpiredToastRef.current &&
        !sessionExpiredToastShownRef.current
      ) {
        sessionExpiredToastShownRef.current = true;
        notify.info('Sua sessão expirou. Faça login novamente.');
      }
      skipSessionExpiredToastRef.current = false;
      clearClientAuthState();
      if (hadSession) redirectAfterSessionLoss();
    };

    const handleSignedIn = (nextSession: Session) => {
      sessionExpiredToastShownRef.current = false;
      const isNewRegistration =
        localStorage.getItem('adlitics_new_registration') === 'true';
      captureEvent(isNewRegistration ? 'user signed up' : 'user signed in', {
        auth_provider: nextSession.user.app_metadata?.provider ?? 'email',
      });
      if (isNewRegistration) {
        markPendingOnboarding();
        setCurrentPage('onboarding');
      }
      void refreshMfaGate();
      localStorage.removeItem('adlitics_new_registration');
    };

    const safetyTimer = window.setTimeout(finishAuthLoading, 2000);
    const handleInvalidSession = () => settleSessionEnd(false);
    window.addEventListener(AUTH_SESSION_INVALID_EVENT, handleInvalidSession);
    void recoverStaleAuthSession().finally(finishAuthLoading);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'INITIAL_SESSION') {
        applySession(nextSession);
        finishAuthLoading();
        return;
      }

      if (nextSession) hadSessionRef.current = true;

      const isRevoked = (event as string) === TOKEN_REFRESH_REVOKED;
      const sessionEnded = event === 'SIGNED_OUT' || isRevoked;

      if (!sessionEnded) {
        const nextUser = nextSession?.user ?? null;
        // Keep User identity stable on JWT refresh (avoids org reload / modal unmount).
        if (event === 'TOKEN_REFRESHED') {
          if (userRef.current?.id !== nextUser?.id) setUser(nextUser);
          setIsAuthenticated(!!nextSession);
          return;
        }
        setUser(nextUser);
        setIsAuthenticated(!!nextSession);
        if (nextSession && event === 'SIGNED_IN') handleSignedIn(nextSession);
        return;
      }

      if (isRevoked) void clearLocalAuthSession();
      settleSessionEnd(true);
    });

    return () => {
      clearTimeout(safetyTimer);
      window.removeEventListener(AUTH_SESSION_INVALID_EVENT, handleInvalidSession);
      subscription.unsubscribe();
    };
  }, [applySession, clearClientAuthState, refreshMfaGate, setCurrentPage]);

  const login = useCallback(async () => {
    // Consent page renders outside AuthenticatedLayout; setCurrentPage would rewrite its URL.
    if (isOnOAuthAuthorize()) return;
    let isSuperadmin = false;
    const uid = userRef.current?.id;
    if (uid) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('global_role')
        .eq('id', uid)
        .maybeSingle();
      isSuperadmin = isSuperadminRole(profile?.global_role);
    }
    const { page } = resolvePostAuthDestination(
      window.location.pathname,
      storagePage('get'),
      { isSuperadmin, returnPath: consumeAuthReturnPath() },
    );
    setCurrentPage(page);
  }, [setCurrentPage]);

  const logout = useCallback(async () => {
    skipSessionExpiredToastRef.current = true;
    hadSessionRef.current = false;
    clearClientAuthState();
    setAuthPage('login');
    storagePage('clear');
    if (window.location.pathname !== AUTH_PATHS.login) {
      replaceHistory(AUTH_PATHS.login);
    }
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch {
      /* refresh já inválido — limpa local abaixo */
    } finally {
      await clearLocalAuthSession();
    }
  }, [clearClientAuthState]);

  const navigateToRegister = useCallback(() => goToAuthPage('register'), [goToAuthPage]);
  const navigateToLogin = useCallback(() => goToAuthPage('login'), [goToAuthPage]);

  const exitInviteToApp = useCallback(() => {
    clearInviteToken();
    setAuthPage('login');
    replaceHistory('/dashboard');
  }, []);

  const contextValue = useMemo(
    () => ({
      isAuthenticated, authPage, login, logout, navigateToRegister, navigateToLogin,
      exitInviteToApp, user, isAuthLoading, mfaGate, refreshMfaGate,
    }),
    [isAuthenticated, authPage, login, logout, navigateToRegister, navigateToLogin,
      exitInviteToApp, user, isAuthLoading, mfaGate, refreshMfaGate],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

// Provider + hook no mesmo arquivo: Fast Refresh invalida o módulo e deixa
// useAuth lendo outro createContext — full reload evita o falso "outside AuthProvider".
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    window.location.reload();
  });
}
